import type { View } from '../router/router';
import { authService } from '../services/auth.service';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { getTyrisIcon } from '../components/ui/tyris-icon';

export class ProfilePage implements View {
  private userData: any = null;

  async render(): Promise<string> {
    const user = authService.getUser();
    if (!user) {
      toast.error('Sesión no encontrada. Redirigiendo al inicio de sesión.');
      setTimeout(() => (window.location.hash = '#/login'), 1500);
      return `<div class="card-glass" style="padding: 2rem; text-align:center;"><p>Cargando perfil...</p></div>`;
    }
    // Fetch fresh data from server
    try {
      this.userData = await apiClient.get<any>(`/api/users/${user.id}`);
    } catch {
      this.userData = user;
    }
    return this.buildHtml();
  }

  private buildHtml(): string {
    const u = this.userData;
    const dob = u.dateOfBirth ? new Date(u.dateOfBirth).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }) : null;
    const sexLabel = u.sex === 'M' ? 'Masculino' : u.sex === 'F' ? 'Femenino' : u.sex === 'O' ? 'Otro' : null;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 style="display:flex;align-items:center;gap:0.6rem;">${getTyrisIcon('perfil', { size: 28 })} Perfil de Usuario</h2>
          <p>Tus datos personales y opciones de cuenta.</p>
        </div>
      </div>

      <div class="card-glass" style="padding: 2rem; max-width: 560px; margin: 2rem auto;">
        <div style="display:flex; flex-direction:column; gap:0.75rem;">
          <h3 style="font-size:1.4rem; margin:0;">${u.firstName} ${u.lastName}</h3>
          <p style="color:var(--text-muted); margin:0;">${u.email}</p>
          <p style="margin:0;">Rol: <span class="badge badge-${u.role.toLowerCase()}">${u.role}</span></p>
          ${u.licenseType ? `<p style="margin:0;">Licencia: <span class="badge">${u.licenseType}</span></p>` : ''}
        </div>

        <hr style="margin:1.5rem 0; border-color:var(--border-metallic);" />

        <div style="display:grid; grid-template-columns:1fr 1fr; gap:0.75rem 1.5rem;">
          ${dob ? `<div><span style="color:var(--text-muted);font-size:0.8rem;display:block;">Fecha de nacimiento</span><span style="font-weight:600;">${dob}</span></div>` : ''}
          ${sexLabel ? `<div><span style="color:var(--text-muted);font-size:0.8rem;display:block;">Sexo</span><span style="font-weight:600;">${sexLabel}</span></div>` : ''}
          ${u.phone ? `<div><span style="color:var(--text-muted);font-size:0.8rem;display:block;">Teléfono</span><span style="font-weight:600;">${u.phone}</span></div>` : ''}
        </div>

        <hr style="margin:1.5rem 0; border-color:var(--border-metallic);" />

        <!-- Edit form -->
        <details style="margin-bottom:1.5rem;">
          <summary style="cursor:pointer; font-weight:600; color:var(--tyris-energy); font-size:0.95rem; margin-bottom:0.5rem; display:inline-flex; align-items:center; gap:0.4rem;">
            ${getTyrisIcon('editar', { size: 16 })} Editar datos personales
          </summary>
          <form id="profile-form" style="display:flex;flex-direction:column;gap:0.75rem;margin-top:1rem;">
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.75rem;">
              <div>
                <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.3rem;">Nombre</label>
                <input id="pf-firstName" type="text" value="${u.firstName}" class="input-field" style="width:100%;" />
              </div>
              <div>
                <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.3rem;">Apellido</label>
                <input id="pf-lastName" type="text" value="${u.lastName}" class="input-field" style="width:100%;" />
              </div>
            </div>
            <div>
              <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.3rem;">Teléfono</label>
              <input id="pf-phone" type="tel" value="${u.phone ?? ''}" class="input-field" style="width:100%;" placeholder="Ej: 099123456" />
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.75rem;">
              <div>
                <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.3rem;">Fecha de nacimiento</label>
                <input id="pf-dob" type="date" value="${u.dateOfBirth ? u.dateOfBirth.substring(0,10) : ''}" class="input-field" style="width:100%;" />
              </div>
              <div>
                <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.3rem;">Sexo</label>
                <select id="pf-sex" class="input-field" style="width:100%;">
                  <option value="">— Sin especificar —</option>
                  <option value="M" ${u.sex === 'M' ? 'selected' : ''}>Masculino</option>
                  <option value="F" ${u.sex === 'F' ? 'selected' : ''}>Femenino</option>
                  <option value="O" ${u.sex === 'O' ? 'selected' : ''}>Otro</option>
                </select>
              </div>
            </div>
            <div>
              <label style="font-size:0.8rem;color:var(--text-muted);display:block;margin-bottom:0.3rem;">Nueva contraseña (dejar vacío para no cambiar)</label>
              <input id="pf-password" type="password" class="input-field" style="width:100%;" placeholder="Mínimo 6 caracteres" />
            </div>
            <button type="submit" id="pf-save" class="btn btn-primary" style="width:100%;padding:0.75rem;display:inline-flex;align-items:center;justify-content:center;gap:0.5rem;">
              ${getTyrisIcon('éxito', { size: 16 })} Guardar cambios
            </button>
          </form>
        </details>

        <button id="btn-logout-profile" class="btn btn-outline" style="width:100%; padding:0.75rem; font-size:0.95rem; display:inline-flex; align-items:center; justify-content:center; gap:0.5rem;">
          ${getTyrisIcon('darse-de-baja', { size: 16 })} Cerrar Sesión
        </button>
      </div>
    `;
  }

  afterRender?(): void {
    document.getElementById('btn-logout-profile')?.addEventListener('click', () => {
      authService.logout();
      toast.info('Sesión finalizada correctamente.');
      window.location.hash = '#/login';
    });

    document.getElementById('profile-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const user = authService.getUser()!;
      const firstName = (document.getElementById('pf-firstName') as HTMLInputElement).value.trim();
      const lastName = (document.getElementById('pf-lastName') as HTMLInputElement).value.trim();
      const phone = (document.getElementById('pf-phone') as HTMLInputElement).value.trim();
      const dateOfBirth = (document.getElementById('pf-dob') as HTMLInputElement).value;
      const sex = (document.getElementById('pf-sex') as HTMLSelectElement).value;
      const password = (document.getElementById('pf-password') as HTMLInputElement).value;

      const body: Record<string, any> = { firstName, lastName };
      if (phone) body.phone = phone;
      if (dateOfBirth) body.dateOfBirth = dateOfBirth;
      if (sex) body.sex = sex;
      if (password) body.password = password;

      try {
        const btn = document.getElementById('pf-save') as HTMLButtonElement;
        btn.disabled = true;
        btn.textContent = 'Guardando...';
        await apiClient.patch(`/api/users/${user.id}`, body);
        toast.success('Perfil actualizado correctamente.');
        // Refresh page data
        this.userData = await apiClient.get<any>(`/api/users/${user.id}`);
        const root = document.querySelector('#app-view-area') ?? document.body;
        root.innerHTML = this.buildHtml();
        this.afterRender?.();
      } catch (err: any) {
        toast.error(err.message || 'Error al actualizar el perfil.');
        const btn = document.getElementById('pf-save') as HTMLButtonElement;
        if (btn) { btn.disabled = false; btn.innerHTML = `${getTyrisIcon('guardar', { size: 16 })} <span>Guardar cambios</span>`; }
      }
    });
  }
}
